import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Format a number as Kwanzas: "100.000,00 Kz" — always full, never abbreviated */
export function formatKz(value: number | null | undefined): string {
  if (value == null) return "0,00 Kz";
  return `${new Intl.NumberFormat("pt-AO", { style: "decimal", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)} Kz`;
}

export function getSafeImageUrls(images: unknown): string[] {
  // Se for nulo ou indefinido
  if (!images) return [];

  // Se for uma string simples (uma única URL ou JSON string)
  if (typeof images === "string") {
    const trimmed = images.trim();
    if (!trimmed) return [];
    
    // Tentar fazer parse se parecer JSON
    if ((trimmed.startsWith("[") || trimmed.startsWith("{"))) {
      try {
        const parsed = JSON.parse(trimmed);
        // Recursivamente processar o resultado do parse
        return getSafeImageUrls(parsed);
      } catch {
        // Se falhar o parse, tratar como URL simples
        return [trimmed];
      }
    }
    
    // String simples (URL direta)
    return [trimmed];
  }

  // Se for array
  if (Array.isArray(images)) {
    return images
      .map((x) => {
        if (typeof x === "string") return x.trim();
        if (x && typeof x === "object") {
          return (
            (x.url as string) ||
            (x.publicUrl as string) ||
            (x.public_url as string) ||
            (x.src as string) ||
            (x.path as string) ||
            (x.thumbnail as string) ||
            ""
          );
        }
        return "";
      })
      .filter((u) => typeof u === "string" && u.trim().length > 0)
      .map((u) => u.trim());
  }

  // Se for objeto único
  if (typeof images === "object" && images !== null) {
    const url = (images as any).url ||
      (images as any).publicUrl ||
      (images as any).public_url ||
      (images as any).src ||
      (images as any).path ||
      (images as any).thumbnail ||
      "";
    return url ? [url.trim()] : [];
  }

  return [];
}

// Placeholder oficial Berastays (SVG branded, leve, sem dependência externa)
export const BERASTAYS_IMAGE_PLACEHOLDER = "/images/property-placeholder.svg";

export function getFirstImageUrl(images: unknown): string {
  const urls = getSafeImageUrls(images);
  return urls[0] || BERASTAYS_IMAGE_PLACEHOLDER;
}

/** Helper to draw a rounded rectangle on a canvas context (backward-compatible) */
function drawRoundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

/** Elegant helper to download a photo/image directly from a URL with a watermark applied */
export async function downloadImage(url: string, title?: string) {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    
    // Load main image
    const img = new Image();
    img.crossOrigin = "anonymous";
    
    const imgLoaded = new Promise<HTMLImageElement>((resolve, reject) => {
      img.onload = () => resolve(img);
      img.onerror = reject;
    });
    
    const blobUrl = URL.createObjectURL(blob);
    img.src = blobUrl;
    
    const loadedImg = await imgLoaded;
    
    // Create canvas
    const canvas = document.createElement("canvas");
    canvas.width = loadedImg.naturalWidth || loadedImg.width;
    canvas.height = loadedImg.naturalHeight || loadedImg.height;
    const ctx = canvas.getContext("2d");
    
    if (!ctx) throw new Error("Could not get 2D context");
    
    // Enable maximum quality image smoothing
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    
    // Draw base image
    ctx.drawImage(loadedImg, 0, 0);
    
    // Clean up base image object URL
    URL.revokeObjectURL(blobUrl);
    
    // Calculate watermark dimensions based on image size (proportional: 3.5% of image height)
    const watermarkHeight = Math.max(20, Math.min(80, canvas.height * 0.035));
    const padding = watermarkHeight * 0.6; // Spacing from the corner
    
    const pinSize = watermarkHeight * 0.8;
    const fontSize = watermarkHeight * 0.75;
    
    // Set up text font with premium semibold system font stack
    ctx.font = `600 ${fontSize}px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif`;
    const textStr = "berastays.com";
    const textWidth = ctx.measureText(textStr).width;
    
    const gap = watermarkHeight * 0.22;
    const totalWidth = pinSize + gap + textWidth;
    
    // Position at bottom-right
    const x = canvas.width - totalWidth - padding;
    const y = canvas.height - watermarkHeight - padding;
    
    // Configure soft drop shadow for maximum readability and zero pixelation/sharpness artifacts
    ctx.save();
    ctx.shadowColor = "rgba(0, 0, 0, 0.4)";
    ctx.shadowBlur = Math.max(2, watermarkHeight * 0.12);
    ctx.shadowOffsetX = Math.max(1, watermarkHeight * 0.04);
    ctx.shadowOffsetY = Math.max(1, watermarkHeight * 0.04);
    
    // Draw vector pin icon directly in pure white (#ffffff)
    const pinCenterX = x + pinSize / 2;
    const pinCenterY = y + pinSize / 2;
    
    ctx.fillStyle = "#ffffff";
    ctx.beginPath();
    // Start at bottom tip
    ctx.moveTo(pinCenterX, pinCenterY + pinSize * 0.45);
    // Left curve up to the top circle
    ctx.bezierCurveTo(
      pinCenterX - pinSize * 0.35, pinCenterY + pinSize * 0.1,
      pinCenterX - pinSize * 0.35, pinCenterY - pinSize * 0.3,
      pinCenterX, pinCenterY - pinSize * 0.3
    );
    // Right curve down to the bottom tip
    ctx.bezierCurveTo(
      pinCenterX + pinSize * 0.35, pinCenterY - pinSize * 0.3,
      pinCenterX + pinSize * 0.35, pinCenterY + pinSize * 0.1,
      pinCenterX, pinCenterY + pinSize * 0.45
    );
    ctx.closePath();
    ctx.fill();
    
    // Draw inner circle hole with destination-out composite mode (transparency through the pin)
    ctx.save();
    ctx.globalCompositeOperation = "destination-out";
    ctx.beginPath();
    ctx.arc(pinCenterX, pinCenterY - pinSize * 0.05, pinSize * 0.14, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    
    // Draw text in pure white (#ffffff)
    ctx.fillStyle = "#ffffff";
    ctx.textBaseline = "middle";
    ctx.fillText(textStr, x + pinSize + gap, y + pinSize / 2);
    
    ctx.restore();
    
    // Detect original image format to prevent quality downgrade or unnecessary format conversion
    const mimeType = blob.type.startsWith("image/") ? blob.type : "image/jpeg";
    
    // Trigger download of the watermarked image in maximum possible quality (1.0 or lossless)
    canvas.toBlob((resultBlob) => {
      if (!resultBlob) {
        // Fallback if canvas blob generation fails
        window.open(url, "_blank");
        return;
      }
      const finalBlobUrl = URL.createObjectURL(resultBlob);
      const link = document.createElement("a");
      link.href = finalBlobUrl;
      
      const extension = mimeType.split("/")[1] || "jpg";
      const filename = (title || "foto").toLowerCase().replace(/[^a-z0-9]/g, "-") + `-watermarked.${extension}`;
      link.download = filename;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(finalBlobUrl);
    }, mimeType, mimeType === "image/png" ? undefined : 1.0);
    
  } catch (error) {
    console.error("Erro ao transferir imagem com marca d'água:", error);
    // Fallback: open in new tab if CORS or other issue prevents canvas manipulation
    window.open(url, "_blank");
  }
}
